import React, { useState, useEffect, useRef } from 'react';
import { Mail, Lock, Eye, EyeOff, User, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../constants/routes.constants';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import Logo from '../common/Logo';
import PhoneInput from '../common/PhoneInput';
import OTPVerification from './OTPVerification';
import { extractCountryCode } from '../../utils/phoneUtils';

/** Redirection post-authentification selon le rôle */
const getPostLoginPath = (userRole, fallbackPath = '/') => {
  if (userRole === 'admin' || userRole === 'super_admin' || userRole === 'staff') {
    return ROUTES.ADMIN_DASHBOARD;
  }
  if (userRole === 'vendor') {
    return ROUTES.VENDOR_DASHBOARD;
  }
  if (userRole === 'user') {
    return ROUTES.USER_DASHBOARD;
  }
  return fallbackPath;
};

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [registeredUser, setRegisteredUser] = useState(null);
  const isSubmitting = useRef(false); // Protection contre les doubles soumissions
  const { t } = useLanguage();
  const { login, register } = useAuth();

  // Bloquer le scroll de la page quand le modal est ouvert
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: '+212',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (isSignUp) {
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'Le prénom est requis';
      }
      
      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Le nom de famille est requis';
      }
      
      if (!formData.phoneNumber.trim()) {
        newErrors.phoneNumber = 'Le numéro de téléphone est requis';
      } else if (formData.phoneNumber.replace(/\D/g, '').length < 8) {
        newErrors.phoneNumber = 'Le numéro de téléphone doit contenir au moins 8 chiffres';
      }
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }
    
    if (isSignUp) {
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Confirmez votre mot de passe';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
      
      if (!formData.agreeTerms) {
        newErrors.agreeTerms = 'Vous devez accepter les conditions';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Protection contre les doubles soumissions
    if (isSubmitting.current) return;
    
    if (!validateForm()) return;
    
    isSubmitting.current = true;
    setIsLoading(true);
    setError(null);
    
    try {
      if (isSignUp) {
        // Extraire le code pays et le numéro depuis formData.phoneNumber
        // Le format est: +212612345678 (code pays + numéro)
        const { countryCode, phoneNumber: phoneNumberOnly } = extractCountryCode(
          formData.phoneNumber,
          formData.countryCode || '+212'
        );
        
        // Inscription
        const result = await register({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: phoneNumberOnly,
          countryCode: countryCode,
          password: formData.password,
          role: 'user',
          userDetails: {
            driverLicense: 'PENDING', // À compléter dans un formulaire étendu
          },
        });

        if (result.success) {
          // Afficher la page de vérification OTP
          setRegisteredUser({
            id: result.data.user.id || result.data.user._id,
            phoneNumber: phoneNumberOnly,
            countryCode: countryCode,
          });
          setShowOTPVerification(true);
        } else {
          // Gérer les erreurs spécifiques pour email et phoneNumber
          if (result.errors) {
            const newErrors = {};
            if (result.errors.email) {
              newErrors.email = result.errors.email;
            }
            if (result.errors.phoneNumber) {
              newErrors.phoneNumber = result.errors.phoneNumber;
            }
            setErrors(prev => ({ ...prev, ...newErrors }));
          }
          setError(result.message || 'Erreur lors de l\'inscription');
        }
      } else {
        // Connexion
        const result = await login(formData.email, formData.password);

        if (result.success) {
          const userRole = result.data?.user?.role;
          navigate(getPostLoginPath(userRole, location.state?.from?.pathname || '/'));
        } else {
          setError(result.message || 'Erreur lors de la connexion');
        }
      }
    } catch (err) {
      // Gérer les erreurs du backend avec les erreurs spécifiques
      if (err.response && err.response.errors) {
        const newErrors = {};
        if (err.response.errors.email) {
          newErrors.email = err.response.errors.email;
        }
        if (err.response.errors.phoneNumber) {
          newErrors.phoneNumber = err.response.errors.phoneNumber;
        }
        setErrors(prev => ({ ...prev, ...newErrors }));
        setError(err.response.message || err.message || 'Une erreur est survenue');
      } else {
        setError(err.message || 'Une erreur est survenue');
      }
    } finally {
      setIsLoading(false);
      isSubmitting.current = false;
    }
  };

  const handleGoogleSignIn = () => {
    // Rediriger vers l'endpoint Google OAuth du backend
    window.location.href = `${ROUTES.API_BASE_URL}${ROUTES.GOOGLE_AUTH}`;
  };

  const switchMode = () => {
    setIsSignUp(!isSignUp);
    setErrors({});
    setError(null); // Réinitialiser aussi l'erreur générale
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      countryCode: '+212',
      password: '',
      confirmPassword: '',
      agreeTerms: false
    });
  };

  // Gérer la vérification OTP réussie
  const handleOTPVerified = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    navigate(getPostLoginPath(user?.role, ROUTES.HOME));
  };

  // Retourner à l'inscription depuis OTP
  const handleBackToSignUp = () => {
    setShowOTPVerification(false);
    setRegisteredUser(null);
    setError(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md">
        {/* Card principale */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          
          {/* Header avec logo et gradient */}
          <div className="relative bg-gradient-to-br from-[#27489E] via-[#1e3a7d] to-[#27489E] px-8 pt-12 pb-16 overflow-hidden">
            {/* Pattern décoratif */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
            </div>
            
            {/* Bouton fermer */}
            <button 
              onClick={() => navigate(-1)}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition p-2 z-20"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Logo - Centré et bien visible */}
            <div className="relative text-center z-10 pt-2">
              <div className="flex justify-center items-center">
                <Logo size="text-5xl" carColor="text-white" vyColor="text-[#F5A623]" />
              </div>
              <p className="text-white/90 mt-3 text-sm font-medium">
                {isSignUp ? 'Créez votre compte' : 'Bienvenue !'}
              </p>
            </div>
          </div>

          {/* Formulaire ou Vérification OTP */}
          <div className="px-8 pb-8 -mt-8">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              
              {/* Afficher la vérification OTP si nécessaire */}
              {showOTPVerification && registeredUser ? (
                <OTPVerification
                  userId={registeredUser.id}
                  phoneNumber={registeredUser.phoneNumber}
                  countryCode={registeredUser.countryCode}
                  onVerified={handleOTPVerified}
                  onClose={handleBackToSignUp}
                />
              ) : (
                <>
              {/* Toggle Sign In / Sign Up */}
              <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
                <button
                  onClick={() => {
                    if (isSignUp) {
                      switchMode();
                    }
                  }}
                  className={`flex-1 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    !isSignUp 
                      ? 'bg-white text-[#27489E] shadow-md' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Connexion
                </button>
                <button
                  onClick={() => {
                    if (!isSignUp) {
                      switchMode();
                    }
                  }}
                  className={`flex-1 py-2.5 rounded-lg font-semibold text-sm transition-all ${
                    isSignUp 
                      ? 'bg-white text-[#27489E] shadow-md' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Inscription
                </button>
              </div>

              {/* Message d'erreur */}
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className={`space-y-${isSignUp ? '3' : '4'}`}>
                
                {/* Prénom et Nom (Sign Up only) */}
                {isSignUp && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Prénom */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Prénom <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="John"
                            className={`w-full pl-11 pr-4 py-2.5 rounded-xl border ${
                              errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-200'
                            } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition text-sm`}
                          />
                        </div>
                        {errors.firstName && (
                          <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                        )}
                      </div>

                      {/* Nom de famille */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Nom de famille <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Doe"
                            className={`w-full pl-11 pr-4 py-2.5 rounded-xl border ${
                              errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-200'
                            } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition text-sm`}
                          />
                        </div>
                        {errors.lastName && (
                          <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                        )}
                      </div>
                    </div>

                    {/* Numéro de téléphone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Numéro de téléphone <span className="text-red-500">*</span>
                      </label>
                      <PhoneInput
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        error={errors.phoneNumber}
                        placeholder="6 12 34 56 78"
                      />
                      {errors.phoneNumber && (
                        <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
                      )}
                    </div>
                  </>
                )}

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Adresse email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="exemple@email.com"
                      className={`w-full pl-11 pr-4 py-2.5 rounded-xl border ${
                        errors.email ? 'border-red-500 bg-red-50' : 'border-gray-200'
                      } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition text-sm`}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Mot de passe */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Mot de passe <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className={`w-full pl-11 pr-12 py-2.5 rounded-xl border ${
                        errors.password ? 'border-red-500 bg-red-50' : 'border-gray-200'
                      } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition text-sm`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                  )}
                </div>

                {/* Confirmer mot de passe (Sign Up only) */}
                {isSignUp && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Confirmer le mot de passe
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="••••••••"
                        className={`w-full pl-11 pr-12 py-2.5 rounded-xl border ${
                          errors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-200'
                        } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition text-sm`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                    )}
                  </div>
                )}

                {/* Mot de passe oublié (Sign In only) */}
                {!isSignUp && (
                  <div className="flex justify-end">
                    <button 
                      type="button"
                      className="text-sm text-[#27489E] hover:text-[#1e3a7d] font-medium"
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                )}

                {/* Accepter les conditions (Sign Up only) */}
                {isSignUp && (
                  <div className="pt-1">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <div className="relative mt-0.5 flex-shrink-0">
                        <input
                          type="checkbox"
                          name="agreeTerms"
                          checked={formData.agreeTerms}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition ${
                          formData.agreeTerms 
                            ? 'bg-[#27489E] border-[#27489E]' 
                            : errors.agreeTerms ? 'border-red-500' : 'border-gray-300'
                        }`}>
                          {formData.agreeTerms && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                      <span className="text-xs text-gray-600 leading-relaxed">
                        J'accepte les{' '}
                        <a href="#" className="text-[#27489E] hover:underline font-medium">Conditions d'utilisation</a>
                        {' '}et la{' '}
                        <a href="#" className="text-[#27489E] hover:underline font-medium">Politique de confidentialité</a>
                      </span>
                    </label>
                    {errors.agreeTerms && (
                      <p className="text-red-500 text-xs mt-1 ml-7">{errors.agreeTerms}</p>
                    )}
                  </div>
                )}

                {/* Bouton Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white ${isSignUp ? 'py-3' : 'py-3.5'} rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#27489E]/30 transition-all disabled:opacity-70 text-sm`}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      {isSignUp ? 'Créer mon compte' : 'Se connecter'}
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>

              {/* Séparateur */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">ou continuer avec</span>
                </div>
              </div>

              {/* Bouton Google */}
              <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition font-medium text-gray-700 disabled:opacity-70"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continuer avec Google
              </button>

              {/* Lien switch mode */}
              <p className="text-center text-sm text-gray-600 mt-6">
                {isSignUp ? 'Vous avez déjà un compte ?' : 'Vous n\'avez pas de compte ?'}{' '}
                <button
                  onClick={switchMode}
                  className="text-[#F5A623] hover:text-[#e09000] font-semibold"
                >
                  {isSignUp ? 'Se connecter' : 'Inscrivez-vous'}
                </button>
              </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Décoration en bas */}
        {!showOTPVerification && (
          <div className="flex justify-center mt-6 gap-2">
            <div className="w-2 h-2 rounded-full bg-[#27489E]"></div>
            <div className="w-2 h-2 rounded-full bg-[#F5A623]"></div>
            <div className="w-2 h-2 rounded-full bg-[#27489E]/50"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthPage;

